import { useContext, useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LanguageContext } from '../App';
import {
  ANDROID_APP_APK_URL,
  APP_VERSION_JSON_URL,
  isKayiComNativeApp,
} from '../utils/androidApp';

const SNOOZE_KEY = 'kayicom_update_snooze';

const readSnooze = () => {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY) || 'null');
  } catch {
    return null;
  }
};

const writeSnooze = (versionCode) => {
  try {
    localStorage.setItem(
      SNOOZE_KEY,
      JSON.stringify({ versionCode, until: Date.now() + 24 * 60 * 60 * 1000 })
    );
  } catch {
    // ignore
  }
};

const parseBuild = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const cmpVersionName = (localName, remoteName) => {
  const a = String(localName || '').split('.').map((p) => Number(p) || 0);
  const b = String(remoteName || '').split('.').map((p) => Number(p) || 0);
  const len = Math.max(a.length, b.length, 3);
  for (let i = 0; i < len; i += 1) {
    const diff = (b[i] || 0) - (a[i] || 0);
    if (diff) return diff;
  }
  return 0;
};

const fill = (template, vars) =>
  String(template || '').replace(/\{(\w+)\}/g, (_, key) => (vars[key] != null ? String(vars[key]) : ''));

const waitForNativeUpdate = () =>
  new Promise((resolve) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve({ ok: false, error: 'timeout' });
    }, 180000);

    const cleanup = () => {
      clearTimeout(timeout);
      try {
        delete window.__kayiUpdateDone;
      } catch {
        window.__kayiUpdateDone = undefined;
      }
    };

    window.__kayiUpdateDone = (result) => {
      cleanup();
      resolve(result || { ok: true });
    };
  });

/**
 * Prefer the native KayiNative JS bridge (always injected into the WebView),
 * then Capacitor ApkUpdater, then fail loudly — never rely on WebView navigation
 * to an .apk URL (Android WebViews usually do nothing).
 */
const runNativeInstall = async (absoluteUrl, t) => {
  if (window.KayiNative && typeof window.KayiNative.downloadAndInstall === 'function') {
    if (typeof window.KayiNative.canInstall === 'function' && !window.KayiNative.canInstall()) {
      toast.message(t('appUpdatePermission'));
      if (typeof window.KayiNative.openInstallSettings === 'function') {
        window.KayiNative.openInstallSettings();
      }
      return { needsPermission: true };
    }
    const done = waitForNativeUpdate();
    window.KayiNative.downloadAndInstall(absoluteUrl);
    return done;
  }

  try {
    const registerPlugin = window.Capacitor?.registerPlugin;
    const plugin = (registerPlugin && registerPlugin('ApkUpdater'))
      || window.Capacitor?.Plugins?.ApkUpdater;
    if (plugin?.downloadAndInstall) {
      const result = await plugin.downloadAndInstall({ url: absoluteUrl });
      if (result?.needsPermission) {
        toast.message(t('appUpdatePermission'));
        if (plugin.openInstallSettings) await plugin.openInstallSettings();
        return { needsPermission: true };
      }
      return { ok: true, ...result };
    }
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }

  return { ok: false, error: 'native-updater-missing' };
};

const AppUpdatePrompt = () => {
  const { t } = useContext(LanguageContext);
  const [update, setUpdate] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isKayiComNativeApp()) return undefined;
    let cancelled = false;

    const run = async () => {
      try {
        const res = await fetch(`${APP_VERSION_JSON_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const remote = await res.json();
        const remoteCode = parseBuild(remote.versionCode);
        const remoteName = String(remote.versionName || '');
        if (!remoteCode && !remoteName) return;

        let localCode = 0;
        let localName = '';
        try {
          const App = window.Capacitor?.Plugins?.App;
          if (App?.getInfo) {
            const info = await App.getInfo();
            localCode = parseBuild(info.build);
            localName = String(info.version || '');
          }
        } catch {
          // ignore
        }

        if (!localName) {
          const match = String(navigator.userAgent || '').match(/KayiComApp\/(\d+(?:\.\d+)*)/);
          if (match) localName = match[1];
        }

        const newerByCode = remoteCode > 0 && localCode > 0 && remoteCode > localCode;
        const newerByName = localCode <= 0 && localName && remoteName && cmpVersionName(localName, remoteName) > 0;
        if (!newerByCode && !newerByName) return;

        const snooze = readSnooze();
        if (
          !remote.force
          && snooze
          && snooze.versionCode === remoteCode
          && Number(snooze.until) > Date.now()
        ) {
          return;
        }

        if (!cancelled) {
          setUpdate({
            versionCode: remoteCode,
            versionName: remoteName,
            force: Boolean(remote.force),
            localName: localName || (localCode ? String(localCode) : ''),
            apkUrl: remote.apkUrl || ANDROID_APP_APK_URL,
          });
        }
      } catch {
        // ignore network errors
      }
    };

    const timer = setTimeout(run, 2200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const startInstantUpdate = async () => {
    if (!update || busy) return;
    const apkUrl = update.apkUrl || ANDROID_APP_APK_URL;
    const absoluteUrl = apkUrl.startsWith('http')
      ? apkUrl
      : `${window.location.origin}${apkUrl.startsWith('/') ? '' : '/'}${apkUrl}`;

    setBusy(true);
    try {
      const result = await runNativeInstall(absoluteUrl, t);
      if (result?.needsPermission) {
        setBusy(false);
        return;
      }
      if (result?.ok === false) {
        toast.error(result.error && result.error !== 'native-updater-missing' && result.error !== 'timeout'
          ? result.error
          : t('appUpdateFailed'));
        // Never trap the user behind a dead modal — always allow dismissing after a failed attempt.
        if (!update.force) {
          writeSnooze(Number(update.versionCode) || 0);
        }
        setBusy(false);
        setUpdate(null);
        return;
      }
      setTimeout(() => setBusy(false), 1200);
    } catch (err) {
      toast.error(err?.message || t('appUpdateFailed'));
      setBusy(false);
    }
  };

  if (!update) return null;

  const force = Boolean(update.force);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
      data-testid="app-update-prompt"
    >
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#141414] p-4 shadow-xl shadow-black/40">
        <p className="text-green-400 text-[11px] font-semibold uppercase tracking-wider mb-1.5">KayiCom</p>
        <h2 id="app-update-title" className="text-white text-lg font-bold mb-1.5">
          {t('appUpdateTitle')}
        </h2>
        <p className="text-white/65 text-sm leading-relaxed mb-2">
          {t('appUpdateMessage')}
        </p>
        <p className="text-white/40 text-xs mb-4">
          {fill(t('appUpdateVersionLine'), {
            local: update.localName || '—',
            remote: update.versionName || '—',
          })}
        </p>
        <div className="flex gap-2 justify-end">
          {!force && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-white/15 text-white/80 hover:bg-white/5"
              disabled={busy}
              onClick={() => {
                writeSnooze(Number(update.versionCode) || 0);
                setUpdate(null);
              }}
            >
              {t('appUpdateLater')}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className="bg-green-500 hover:bg-green-600 text-black font-bold"
            disabled={busy}
            onClick={startInstantUpdate}
            data-testid="app-update-download-btn"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={15} />
                {t('appUpdateInstalling')}
              </>
            ) : (
              <>
                <Download className="mr-2" size={15} />
                {t('appUpdateNow')}
              </>
            )}
          </Button>
        </div>
        <p className="text-white/35 text-[11px] mt-3 leading-relaxed">
          {t('appUpdatePlayProtectHint')}
        </p>
      </div>
    </div>
  );
};

export default AppUpdatePrompt;
